import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { IconPlus, IconSchool } from "@tabler/icons-react";
import { Badge, Button, Group, Loader, Text, Title } from "@mantine/core";
import "./OrganizationContent.css";

function AdminTabProgramsPage() {
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();

  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programStatusFilter, setProgramStatusFilter] = useState("all");

  const canManagePrograms =
    user && (user.role === "MANAGER" || user.role === "COMPANY_ADMIN");

  const fetchPrograms = async () => {
    try {
      setProgramsLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/programs/manager/list`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch programs data");
      }

      const result = await response.json();
      setPrograms(result.data || []);
    } catch (error) {
      setPrograms([]);
    } finally {
      setProgramsLoading(false);
    }
  };

  useEffect(() => {
    if (!canManagePrograms) return;
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const getProgramDisplayStatus = program => {
    if (program.status === "DISABLED") return "DISABLED";
    const hasGroups =
      program.group_programs && program.group_programs.length > 0;
    return hasGroups ? "ACTIVE" : "DRAFT";
  };

  const filteredPrograms = programs.filter(program => {
    const displayStatus = getProgramDisplayStatus(program);
    if (programStatusFilter === "all") return true;
    if (programStatusFilter === "active") return displayStatus === "ACTIVE";
    if (programStatusFilter === "draft") return displayStatus === "DRAFT";
    if (programStatusFilter === "disabled") return displayStatus === "DISABLED";
    return true;
  });

  const activeCount = programs.filter(
    program => getProgramDisplayStatus(program) === "ACTIVE"
  ).length;
  const draftCount = programs.filter(
    program => getProgramDisplayStatus(program) === "DRAFT"
  ).length;
  const disabledCount = programs.filter(
    program => getProgramDisplayStatus(program) === "DISABLED"
  ).length;

  if (programsLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="lg" />
        <Text>Loading programs...</Text>
      </Group>
    );
  }

  return (
    <div className="org-section-content">
      <div className="carousel-section">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>Programs</Title>
            <Text c="dimmed" size="sm">
              {programs.length} {programs.length === 1 ? "program" : "programs"}
            </Text>
          </div>
          {canManagePrograms && (
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate("/programs/new")}
              variant="default"
              size="md"
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                color: "#495057",
              }}
            >
              New Program
            </Button>
          )}
        </Group>

        <Group gap="xs" mb="xl">
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setProgramStatusFilter("all")}
            style={{
              backgroundColor:
                programStatusFilter === "all" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: programStatusFilter === "all" ? 600 : 400,
            }}
          >
            All
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setProgramStatusFilter("active")}
            style={{
              backgroundColor:
                programStatusFilter === "active" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: programStatusFilter === "active" ? 600 : 400,
            }}
          >
            Active ({activeCount})
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setProgramStatusFilter("draft")}
            style={{
              backgroundColor:
                programStatusFilter === "draft" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: programStatusFilter === "draft" ? 600 : 400,
            }}
          >
            Drafts ({draftCount})
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setProgramStatusFilter("disabled")}
            style={{
              backgroundColor:
                programStatusFilter === "disabled" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: programStatusFilter === "disabled" ? 600 : 400,
            }}
          >
            Inactive ({disabledCount})
          </Button>
        </Group>

        {filteredPrograms.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" fs="italic">
            No programs in this category
          </Text>
        ) : (
          <div className="course-cards-grid">
            {filteredPrograms.map((program, index) => {
              const gradients = [
                "linear-gradient(135deg, #ffd0a0 0%, #ffb347 100%)",
                "linear-gradient(135deg, #ffbe7a 0%, #ffa040 100%)",
                "linear-gradient(135deg, #ffaa6a 0%, #ff8c30 100%)",
                "linear-gradient(135deg, #ff9a55 0%, #ff7820 100%)",
              ];
              const displayStatus = getProgramDisplayStatus(program);
              const statusColor =
                displayStatus === "ACTIVE"
                  ? "green"
                  : displayStatus === "DISABLED"
                    ? "red"
                    : "gray";

              return (
                <div
                  key={program.id}
                  className="course-card program-card"
                  onClick={() => navigate(`/programs/edit/${program.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/programs/edit/${program.id}`);
                    }
                  }}
                >
                  <div className="course-card__thumbnail">
                    {program.cover_image ? (
                      <img
                        src={program.cover_image}
                        alt={program.program_name}
                        className="course-card__image"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="course-card__placeholder"
                        style={{ background: gradients[index % 4] }}
                      >
                        <IconSchool size={44} color="rgba(255,255,255,0.85)" />
                      </div>
                    )}
                    <div className="course-card__gradient-overlay" />
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                      }}
                    >
                      <Badge color={statusColor} variant="filled" size="sm">
                        {displayStatus === "DISABLED"
                          ? "INACTIVE"
                          : displayStatus}
                      </Badge>
                    </div>
                  </div>

                  <div className="course-card__content">
                    <h3 className="course-card__title">
                      {program.program_name}
                    </h3>
                    <p className="course-card__desc">
                      {program.tasks?.length || 0}{" "}
                      {(program.tasks?.length || 0) === 1 ? "task" : "tasks"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminTabProgramsPage;
